import React, { useState } from 'react';
import { 
  Container, 
  Form, 
  Button, 
  Card, 
  Row, 
  Col, 
  Alert,
  Modal,
  Badge,
  Accordion
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faTrash,
  faEye,
  faSchool,
  faGraduationCap,
  faUsers,
  faChalkboardTeacher
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { 
  guardarEstructuraEducativaCompleta, 
  validarEstructuraEducativa 
} from '../../services/estructuraEducativaService';
import styles from './ConfiguracionEstructuraEducativa.module.scss';

const MySwal = withReactContent(Swal);

// Datos de referencia
const JURISDICCIONES = [
  "Provincia de Buenos Aires",
  "Ciudad Autónoma de Buenos Aires", 
  "Santa Fe",
  "Córdoba",
  "Mendoza",
  "Tucumán",
  "Entre Ríos",
  "Salta",
  "Misiones",
  "Chaco",
  "Corrientes",
  "Santiago del Estero",
  "San Juan",
  "Jujuy",
  "Río Negro",
  "Neuquén",
  "Formosa",
  "Chubut",
  "San Luis",
  "Catamarca",
  "La Rioja",
  "La Pampa",
  "Santa Cruz",
  "Tierra del Fuego",
  "Otra (especificar)"
];

const ROLES_DOCENTES = [
  "Maestro/a de Grado Titular",
  "Maestro/a Co-enseñante", 
  "Profesor/a de Disciplina",
  "Maestro/a de Área (Especialista)",
  "Maestro/a de Apoyo/Inclusión",
  "Preceptor/a",
  "Ayudante de Sala",
  "Otro (especificar)"
];

const MATERIAS_PRIMARIA = [
  "Prácticas del Lenguaje",
  "Matemática", 
  "Ciencias Sociales",
  "Ciencias Naturales",
  "Educación Física",
  "Educación Artística (Música)",
  "Educación Artística (Plástica)",
  "Inglés",
  "Tecnología",
  "Otro (especificar)"
];

const MATERIAS_SECUNDARIA = [
  "Lengua y Literatura",
  "Matemática",
  "Historia", 
  "Geografía",
  "Biología",
  "Física",
  "Química",
  "Educación Física",
  "Educación Artística",
  "Inglés",
  "Educación Tecnológica",
  "Filosofía",
  "Economía",
  "Otro (especificar)"
];

export const ConfiguracionEstructuraEducativa = () => {
  // Estado principal del formulario
  const [formData, setFormData] = useState({
    // Datos de identificación
    nombreColegio: '',
    cue: '',
    jurisdiccion: '',
    direccion: '',
    telefono: '',
    email: '',
    
    // Estructura educativa
    niveles: [],
    ciclos: [],
    grados: [],
    asignacionesDocentes: [],
    
    // Roles de apoyo
    tieneGabinete: false,
    gabineteProfesionales: [],
    tienePreceptores: false,
    preceptores: []
  });

  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Funciones para manejar niveles educativos
  const agregarNivel = () => {
    const nuevoNivel = {
      id: `nivel_${Date.now()}`,
      nombre: '',
      descripcion: '',
      tieneCiclos: false,
      ciclos: [],
      grados: []
    };
    
    setFormData(prev => ({
      ...prev,
      niveles: [...prev.niveles, nuevoNivel]
    }));
  };

  const eliminarNivel = (nivelId) => {
    MySwal.fire({
      title: '¿Eliminar nivel?',
      text: 'Esta acción eliminará también todos los ciclos y grados asociados',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => ({
          ...prev,
          niveles: prev.niveles.filter(n => n.id !== nivelId),
          ciclos: prev.ciclos.filter(c => c.nivelId !== nivelId),
          grados: prev.grados.filter(g => g.nivelId !== nivelId)
        }));
      }
    });
  };

  const actualizarNivel = (nivelId, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      niveles: prev.niveles.map(n => 
        n.id === nivelId ? { ...n, [campo]: valor } : n
      )
    }));
  };

  // Funciones para manejar ciclos
  const agregarCiclo = (nivelId) => {
    const nuevoCiclo = {
      id: `ciclo_${Date.now()}`,
      nivelId,
      nombre: '',
      descripcion: '',
      gradoDesde: 1,
      gradoHasta: 2,
      grados: []
    };
    
    setFormData(prev => ({
      ...prev,
      ciclos: [...prev.ciclos, nuevoCiclo]
    }));
  };

  const eliminarCiclo = (cicloId) => {
    MySwal.fire({
      title: '¿Eliminar ciclo?',
      text: 'Esta acción eliminará también todos los grados asociados',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => ({
          ...prev,
          ciclos: prev.ciclos.filter(c => c.id !== cicloId),
          grados: prev.grados.filter(g => g.cicloId !== cicloId)
        }));
      }
    });
  };

  const actualizarCiclo = (cicloId, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      ciclos: prev.ciclos.map(c => 
        c.id === cicloId ? { ...c, [campo]: valor } : c
      )
    }));
  };

  // Funciones para manejar grados
  const agregarGrado = (nivelId, cicloId = null) => {
    const nuevoGrado = {
      id: `grado_${Date.now()}`,
      nivelId,
      cicloId,
      nombre: '',
      capacidadMaxima: 30,
      docentes: []
    };
    
    setFormData(prev => ({
      ...prev,
      grados: [...prev.grados, nuevoGrado]
    }));
  };

  const eliminarGrado = (gradoId) => {
    MySwal.fire({
      title: '¿Eliminar grado?',
      text: 'Esta acción eliminará también todas las asignaciones docentes',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => ({
          ...prev,
          grados: prev.grados.filter(g => g.id !== gradoId),
          asignacionesDocentes: prev.asignacionesDocentes.filter(a => a.gradoId !== gradoId)
        }));
      }
    });
  };

  const actualizarGrado = (gradoId, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      grados: prev.grados.map(g => 
        g.id === gradoId ? { ...g, [campo]: valor } : g
      )
    }));
  };

  // Funciones para manejar asignaciones docentes
  const agregarAsignacionDocente = (gradoId) => {
    const nuevaAsignacion = {
      id: `asignacion_${Date.now()}`,
      gradoId,
      dni: '',
      nombreCompleto: '',
      rol: '',
      materias: [],
      horasCatedra: 0,
      observaciones: ''
    };
    
    setFormData(prev => ({
      ...prev,
      asignacionesDocentes: [...prev.asignacionesDocentes, nuevaAsignacion]
    }));
  };

  const eliminarAsignacionDocente = (asignacionId) => {
    setFormData(prev => ({
      ...prev,
      asignacionesDocentes: prev.asignacionesDocentes.filter(a => a.id !== asignacionId)
    }));
  };

  const actualizarAsignacionDocente = (asignacionId, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      asignacionesDocentes: prev.asignacionesDocentes.map(a => 
        a.id === asignacionId ? { ...a, [campo]: valor } : a
      )
    }));
  };

  // Función para obtener materias según el nivel
  const obtenerMaterias = (nivel) => {
    if (nivel.toLowerCase().includes('primaria')) {
      return MATERIAS_PRIMARIA;
    } else if (nivel.toLowerCase().includes('secundaria')) {
      return MATERIAS_SECUNDARIA;
    }
    return [...MATERIAS_PRIMARIA, ...MATERIAS_SECUNDARIA];
  };

  // Función para validar el formulario
  const validarFormulario = () => {
    return validarEstructuraEducativa(formData);
  };

  // Función para guardar la configuración
  const guardarConfiguracion = async () => {
    const errores = validarFormulario();
    
    if (errores.length > 0) {
      MySwal.fire({
        title: 'Errores de validación',
        html: errores.map(error => `<p>• ${error}</p>`).join(''),
        icon: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Guardar en Firebase usando el servicio
      const resultado = await guardarEstructuraEducativaCompleta(formData);
      
      MySwal.fire({
        title: '¡Éxito!',
        text: resultado.mensaje,
        icon: 'success',
        html: `
          <p>${resultado.mensaje}</p>
          <p><strong>ID de la Escuela:</strong> ${resultado.escuelaId}</p>
          <p><strong>Niveles creados:</strong> ${resultado.nivelesIds.length}</p>
          <p><strong>Ciclos creados:</strong> ${resultado.ciclosIds.length}</p>
          <p><strong>Grados creados:</strong> ${resultado.gradosIds.length}</p>
        `
      });
    } catch (error) {
      MySwal.fire({
        title: 'Error',
        text: 'Error al guardar la configuración: ' + error.message,
        icon: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para reiniciar el formulario
  const reiniciarFormulario = () => {
    MySwal.fire({
      title: '¿Reiniciar formulario?',
      text: 'Se perderán todos los datos ingresados',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, reiniciar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData({
          nombreColegio: '',
          cue: '',
          jurisdiccion: '',
          direccion: '',
          telefono: '',
          email: '',
          niveles: [],
          ciclos: [],
          grados: [],
          asignacionesDocentes: [],
          tieneGabinete: false,
          gabineteProfesionales: [],
          tienePreceptores: false,
          preceptores: []
        });
      }
    });
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="text-center mb-3">
            <FontAwesomeIcon icon={faSchool} className="me-3" />
            Configuración de Estructura Educativa
          </h1>
          <p className="text-center text-muted">
            Configure la estructura educativa de su institución de forma personalizada
          </p>
        </Col>
      </Row>

      <Form>
        {/* I. Datos de Identificación del Colegio */}
        <Card className="mb-4">
          <Card.Header>
            <h4>
              <FontAwesomeIcon icon={faSchool} className="me-2" />
              I. Datos de Identificación del Colegio
            </h4>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre del Colegio *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombreColegio}
                    onChange={(e) => setFormData(prev => ({ ...prev, nombreColegio: e.target.value }))}
                    placeholder="Ej: Escuela Primaria N° 123"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>CUE (Código Único de Establecimiento) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.cue}
                    onChange={(e) => setFormData(prev => ({ ...prev, cue: e.target.value }))}
                    placeholder="Ej: 12345678"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Jurisdicción *</Form.Label>
                  <Form.Select
                    value={formData.jurisdiccion}
                    onChange={(e) => setFormData(prev => ({ ...prev, jurisdiccion: e.target.value }))}
                  >
                    <option value="">Seleccione una jurisdicción</option>
                    {JURISDICCIONES.map(jurisdiccion => (
                      <option key={jurisdiccion} value={jurisdiccion}>
                        {jurisdiccion}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="escuela@ejemplo.edu.ar"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dirección</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.direccion}
                    onChange={(e) => setFormData(prev => ({ ...prev, direccion: e.target.value }))}
                    placeholder="Av. Principal 123"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.telefono}
                    onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                    placeholder="011-1234-5678"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* II. Definición de Niveles Educativos */}
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h4>
              <FontAwesomeIcon icon={faGraduationCap} className="me-2" />
              II. Definición de Niveles Educativos
            </h4>
            <Button variant="success" onClick={agregarNivel}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Agregar Nivel
            </Button>
          </Card.Header>
          <Card.Body>
            {formData.niveles.length === 0 ? (
              <Alert variant="info">
                No hay niveles configurados. Haga clic en "Agregar Nivel" para comenzar.
              </Alert>
            ) : (
              <Accordion className={styles['nivel-accordion']}>
                {formData.niveles.map((nivel, index) => (
                  <Accordion.Item key={nivel.id} eventKey={nivel.id} className="mb-3 border-primary">
                    <Accordion.Header className="bg-primary text-white">
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <div>
                          <h5 className="mb-0 text-white">Nivel {index + 1}: {nivel.nombre || 'Sin nombre'}</h5>
                        </div>
                        <Button 
                          variant="outline-light" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            eliminarNivel(nivel.id);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Nombre del Nivel *</Form.Label>
                            <Form.Control
                              type="text"
                              value={nivel.nombre}
                              onChange={(e) => actualizarNivel(nivel.id, 'nombre', e.target.value)}
                              placeholder="Ej: Educación Primaria"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>¿Este Nivel se divide en Ciclos?</Form.Label>
                            <div>
                              <Form.Check
                                inline
                                type="radio"
                                label="Sí"
                                checked={nivel.tieneCiclos}
                                onChange={() => actualizarNivel(nivel.id, 'tieneCiclos', true)}
                              />
                              <Form.Check
                                inline
                                type="radio"
                                label="No"
                                checked={!nivel.tieneCiclos}
                                onChange={() => actualizarNivel(nivel.id, 'tieneCiclos', false)}
                              />
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label>Descripción del Nivel (Opcional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={nivel.descripcion}
                          onChange={(e) => actualizarNivel(nivel.id, 'descripcion', e.target.value)}
                          placeholder="Ej: Incluye las salas de jardín de infantes de 3 a 5 años"
                        />
                      </Form.Group>

                      {/* Ciclos del nivel */}
                      {nivel.tieneCiclos && (
                        <div className="mt-4">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6>
                              <FontAwesomeIcon icon={faUsers} className="me-2" />
                              Ciclos de {nivel.nombre}
                            </h6>
                            <Button variant="outline-primary" size="sm" onClick={() => agregarCiclo(nivel.id)}>
                              <FontAwesomeIcon icon={faPlus} className="me-2" />
                              Agregar Ciclo
                            </Button>
                          </div>
                          <Accordion className={styles['ciclo-accordion']}>
                            {formData.ciclos
                              .filter(ciclo => ciclo.nivelId === nivel.id)
                              .map((ciclo, cicloIndex) => (
                                <Accordion.Item key={ciclo.id} eventKey={`ciclo-${ciclo.id}`} className="mb-2 border-secondary">
                                  <Accordion.Header className="bg-secondary text-white">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                      <div>
                                        <h6 className="mb-0 text-white">Ciclo {cicloIndex + 1}: {ciclo.nombre || 'Sin nombre'}</h6>
                                      </div>
                                      <Button 
                                        variant="outline-light" 
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          eliminarCiclo(ciclo.id);
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </Button>
                                    </div>
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <Row>
                                      <Col md={4}>
                                        <Form.Group className="mb-3">
                                          <Form.Label>Nombre del Ciclo *</Form.Label>
                                          <Form.Control
                                            type="text"
                                            value={ciclo.nombre}
                                            onChange={(e) => actualizarCiclo(ciclo.id, 'nombre', e.target.value)}
                                            placeholder="Ej: Primer Ciclo"
                                          />
                                        </Form.Group>
                                      </Col>
                                      <Col md={4}>
                                        <Form.Group className="mb-3">
                                          <Form.Label>Desde Grado</Form.Label>
                                          <Form.Select
                                            value={ciclo.gradoDesde}
                                            onChange={(e) => actualizarCiclo(ciclo.id, 'gradoDesde', parseInt(e.target.value))}
                                          >
                                            {[1,2,3,4,5,6,7].map(num => (
                                              <option key={num} value={num}>{num}°</option>
                                            ))}
                                          </Form.Select>
                                        </Form.Group>
                                      </Col>
                                      <Col md={4}>
                                        <Form.Group className="mb-3">
                                          <Form.Label>Hasta Grado</Form.Label>
                                          <Form.Select
                                            value={ciclo.gradoHasta}
                                            onChange={(e) => actualizarCiclo(ciclo.id, 'gradoHasta', parseInt(e.target.value))}
                                          >
                                            {[1,2,3,4,5,6,7].map(num => (
                                              <option key={num} value={num}>{num}°</option>
                                            ))}
                                          </Form.Select>
                                        </Form.Group>
                                      </Col>
                                    </Row>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Descripción del Ciclo (Opcional)</Form.Label>
                                      <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={ciclo.descripcion}
                                        onChange={(e) => actualizarCiclo(ciclo.id, 'descripcion', e.target.value)}
                                        placeholder="Ej: En esta etapa se afianza la alfabetización inicial"
                                      />
                                    </Form.Group>
                                  </Accordion.Body>
                                </Accordion.Item>
                              ))}
                          </Accordion>
                        </div>
                      )}

                      {/* Grados del nivel */}
                      <div className="mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6>
                            <FontAwesomeIcon icon={faChalkboardTeacher} className="me-2" />
                            Grados de {nivel.nombre}
                          </h6>
                          <Button variant="outline-success" size="sm" onClick={() => agregarGrado(nivel.id)}>
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Agregar Grado
                          </Button>
                        </div>
                        <Accordion className={styles['grado-accordion']}>
                          {formData.grados
                            .filter(grado => grado.nivelId === nivel.id)
                            .map((grado, gradoIndex) => (
                              <Accordion.Item key={grado.id} eventKey={`grado-${grado.id}`} className="mb-2 border-success">
                                <Accordion.Header className="bg-success text-white">
                                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                    <div>
                                      <h6 className="mb-0 text-white">Grado {gradoIndex + 1}: {grado.nombre || 'Sin nombre'}</h6>
                                    </div>
                                    <Button 
                                      variant="outline-light" 
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        eliminarGrado(grado.id);
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                  <Row>
                                    <Col md={4}>
                                      <Form.Group className="mb-3">
                                        <Form.Label>Nombre del Grado *</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={grado.nombre}
                                          onChange={(e) => actualizarGrado(grado.id, 'nombre', e.target.value)}
                                          placeholder="Ej: 1° Grado"
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Group className="mb-3">
                                        <Form.Label>Ciclo (Si aplica)</Form.Label>
                                        <Form.Select
                                          value={grado.cicloId || ''}
                                          onChange={(e) => actualizarGrado(grado.id, 'cicloId', e.target.value || null)}
                                        >
                                          <option value="">Sin ciclo</option>
                                          {formData.ciclos
                                            .filter(ciclo => ciclo.nivelId === nivel.id)
                                            .map(ciclo => (
                                              <option key={ciclo.id} value={ciclo.id}>
                                                {ciclo.nombre}
                                              </option>
                                            ))}
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Group className="mb-3">
                                        <Form.Label>Capacidad Máxima</Form.Label>
                                        <Form.Control
                                          type="number"
                                          value={grado.capacidadMaxima}
                                          onChange={(e) => actualizarGrado(grado.id, 'capacidadMaxima', parseInt(e.target.value))}
                                          placeholder="30"
                                        />
                                      </Form.Group>
                                    </Col>
                                  </Row>

                                  {/* Asignaciones docentes del grado */}
                                  <div className="mt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <h6>Asignaciones Docentes</h6>
                                      <Button variant="outline-info" size="sm" onClick={() => agregarAsignacionDocente(grado.id)}>
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                        Agregar Docente
                                      </Button>
                                    </div>
                                    <Accordion className={styles['asignacion-accordion']}>
                                      {formData.asignacionesDocentes
                                        .filter(asignacion => asignacion.gradoId === grado.id)
                                        .map((asignacion, asignacionIndex) => (
                                          <Accordion.Item key={asignacion.id} eventKey={`asignacion-${asignacion.id}`} className="mb-2 border-info">
                                            <Accordion.Header className="bg-info text-white">
                                              <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                                <div>
                                                  <h6 className="mb-0 text-white">Docente {asignacionIndex + 1}: {asignacion.nombreCompleto || 'Sin nombre'}</h6>
                                                </div>
                                                <Button 
                                                  variant="outline-light" 
                                                  size="sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    eliminarAsignacionDocente(asignacion.id);
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                              </div>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                              <Row>
                                                <Col md={6}>
                                                  <Form.Group className="mb-3">
                                                    <Form.Label>DNI *</Form.Label>
                                                    <Form.Control
                                                      type="text"
                                                      value={asignacion.dni}
                                                      onChange={(e) => actualizarAsignacionDocente(asignacion.id, 'dni', e.target.value)}
                                                      placeholder="12345678"
                                                    />
                                                  </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                  <Form.Group className="mb-3">
                                                    <Form.Label>Nombre y Apellido *</Form.Label>
                                                    <Form.Control
                                                      type="text"
                                                      value={asignacion.nombreCompleto}
                                                      onChange={(e) => actualizarAsignacionDocente(asignacion.id, 'nombreCompleto', e.target.value)}
                                                      placeholder="María González"
                                                    />
                                                  </Form.Group>
                                                </Col>
                                              </Row>
                                              <Row>
                                                <Col md={6}>
                                                  <Form.Group className="mb-3">
                                                    <Form.Label>Rol *</Form.Label>
                                                    <Form.Select
                                                      value={asignacion.rol}
                                                      onChange={(e) => actualizarAsignacionDocente(asignacion.id, 'rol', e.target.value)}
                                                    >
                                                      <option value="">Seleccione un rol</option>
                                                      {ROLES_DOCENTES.map(rol => (
                                                        <option key={rol} value={rol}>{rol}</option>
                                                      ))}
                                                    </Form.Select>
                                                  </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                  <Form.Group className="mb-3">
                                                    <Form.Label>Horas Cátedra</Form.Label>
                                                    <Form.Control
                                                      type="number"
                                                      value={asignacion.horasCatedra}
                                                      onChange={(e) => actualizarAsignacionDocente(asignacion.id, 'horasCatedra', parseInt(e.target.value))}
                                                      placeholder="25"
                                                    />
                                                  </Form.Group>
                                                </Col>
                                              </Row>
                                              <Form.Group className="mb-3">
                                                <Form.Label>Materias a Cargo</Form.Label>
                                                <div className={styles['checkbox-grid']}>
                                                  {obtenerMaterias(nivel.nombre).map(materia => (
                                                    <Form.Check
                                                      key={materia}
                                                      type="checkbox"
                                                      label={materia}
                                                      checked={asignacion.materias.includes(materia)}
                                                      onChange={(e) => {
                                                        const materias = e.target.checked
                                                          ? [...asignacion.materias, materia]
                                                          : asignacion.materias.filter(m => m !== materia);
                                                        actualizarAsignacionDocente(asignacion.id, 'materias', materias);
                                                      }}
                                                    />
                                                  ))}
                                                </div>
                                              </Form.Group>
                                              <Form.Group className="mb-3">
                                                <Form.Label>Observaciones</Form.Label>
                                                <Form.Control
                                                  as="textarea"
                                                  rows={2}
                                                  value={asignacion.observaciones}
                                                  onChange={(e) => actualizarAsignacionDocente(asignacion.id, 'observaciones', e.target.value)}
                                                  placeholder="Observaciones adicionales sobre este docente"
                                                />
                                              </Form.Group>
                                            </Accordion.Body>
                                          </Accordion.Item>
                                        ))}
                                    </Accordion>
                                  </div>
                                </Accordion.Body>
                              </Accordion.Item>
                            ))}
                        </Accordion>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </Card.Body>
        </Card>

        {/* Botones de acción */}
        <Row className="mb-4">
          <Col className="d-flex justify-content-center gap-3">
            <Button 
              variant="primary" 
              size="lg"
              onClick={guardarConfiguracion}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
            <Button 
              variant="secondary" 
              size="lg"
              onClick={reiniciarFormulario}
              disabled={isSubmitting}
            >
              Reiniciar Formulario
            </Button>
            <Button 
              variant="info" 
              size="lg"
              onClick={() => setShowPreview(true)}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faEye} className="me-2" />
              Previsualizar
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Modal de previsualización */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Previsualización de la Estructura Educativa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="preview-structure">
            <h4>{formData.nombreColegio}</h4>
            <p><strong>CUE:</strong> {formData.cue}</p>
            <p><strong>Jurisdicción:</strong> {formData.jurisdiccion}</p>
            
            <hr />
            
            <h5>Estructura Educativa:</h5>
            {formData.niveles.map((nivel, index) => (
              <div key={nivel.id} className="mb-4">
                <h6>
                  <Badge bg="primary" className="me-2">{index + 1}</Badge>
                  {nivel.nombre}
                </h6>
                {nivel.descripcion && <p className="text-muted">{nivel.descripcion}</p>}
                
                {nivel.tieneCiclos && (
                  <div className="ms-4">
                    <h6>Ciclos:</h6>
                    {formData.ciclos
                      .filter(ciclo => ciclo.nivelId === nivel.id)
                      .map(ciclo => (
                        <div key={ciclo.id} className="mb-2">
                          <Badge bg="secondary" className="me-2">Ciclo</Badge>
                          {ciclo.nombre} ({ciclo.gradoDesde}° a {ciclo.gradoHasta}°)
                        </div>
                      ))}
                  </div>
                )}
                
                <div className="ms-4">
                  <h6>Grados:</h6>
                  {formData.grados
                    .filter(grado => grado.nivelId === nivel.id)
                    .map(grado => (
                      <div key={grado.id} className="mb-2">
                        <Badge bg="success" className="me-2">Grado</Badge>
                        {grado.nombre} (Capacidad: {grado.capacidadMaxima})
                        <div className="ms-4">
                          <small>Docentes: {formData.asignacionesDocentes.filter(a => a.gradoId === grado.id).length}</small>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}; 